import {PromotionType} from "../../definitions/types";
import {PromotionEnumType, VisibilityType} from "../../definitions/enums";
import {currencyFormatter} from "../../utils/helpers";

export class PromotionModel {
    private readonly _type: PromotionEnumType;
    private readonly _campaign: string;
    private readonly _internalDescription: string;
    private readonly _discountFlat?: number;
    private readonly _discountPercent?: number;
    private readonly _minAmount?: number;
    private readonly _minQuantity?: number;
    private readonly _statusCode: VisibilityType;

    constructor(public readonly promotion: PromotionType) {
        this._type = promotion.type;
        this._campaign = promotion.campaign;
        this._internalDescription = promotion.description;
        this._discountFlat = promotion.discountFlat;
        this._discountPercent = promotion.discountPercent;
        this._minAmount = promotion.minAmount;
        this._minQuantity = promotion.minQuantity;
        this._statusCode = promotion.statusCode;
    }

    /**
     * @method type
     * @description Returns the type of the promotion (if any).
     * @returns {PromotionEnumType}
     */
    public get type(): PromotionEnumType {
        return this._type;
    }

    /**
     * @method campaign
     * @description Returns the campaign name of the promotion (if any).
     * @returns {string}
     */
    public get campaign(): string {
        return this._campaign;
    }

    /**
     * @method internalDescription
     * @description Returns the description of the promotion (if any).
     * @returns {string}
     */
    public get internalDescription(): string {
        return this._internalDescription;
    }

    /**
     * @property discount
     * @description Returns the discount of the promotion (if any).
     * @returns {String}
     */
    public get discount(): string {
        if (this.isFlat && this.isPercent) throw new Error(`Promotion cannot be both flat and percent.`);
        if (!this.isFlat && !this.isPercent) throw new Error(`Promotion must be either flat or percent.`);
        if (this.isPercent) return `${this.discountPercent}%`;
        if (!this.discountFlat) throw new Error(`Promotion must have a discount.`);

        // If the min amount is a whole dollar amount, remove the cents.
        return Math.floor(this.discountFlat) === this.discountFlat
            ? currencyFormatter.format(this.discountFlat).replace(/\.00$/, '')
            : currencyFormatter.format(this.discountFlat);
    }

    /**
     * @property discountFlat
     * @description Returns the flat discount of the promotion (if any).
     * @returns {number | undefined}
     */
    public get discountFlat(): number | undefined {
        return this._discountFlat;
    }

    /**
     * @property discountPercent
     * @description Returns the percent discount of the promotion (if any).
     * @returns {number | undefined}
     */
    public get discountPercent(): number | undefined {
        return this._discountPercent;
    }

    /**
     * @property statusCode
     * @description Returns the statusCode of the promotion (if any).
     * @returns {VisibilityType}
     */
    public get statusCode(): VisibilityType {
        return this._statusCode;
    }
    
    /**
     * @property minAmount
     * @description Returns the minimum amount of the promotion (if any).
     * @returns {number | undefined}
     */
    public get minAmount(): number | undefined {
        return this._minAmount;
    }

    /**
     * @property formattedMinAmount
     * @description Returns the formatted minimum amount of the promotion (if any).
     * @returns {string | undefined}
     */
    public get formattedMinAmount(): string | undefined {
        if (!this.minAmount) return undefined;

        // If the min amount is a whole dollar amount, remove the cents.
        return Math.floor(this.minAmount) === this.minAmount
            ? currencyFormatter.format(this.minAmount).replace(/\.00$/, '')
            : currencyFormatter.format(this.minAmount);
    }

    /**
     * @property minQuantity
     * @description Returns the minimum quantity of the promotion (if any).
     * @returns {number | undefined}
     */
    public get minQuantity(): number | undefined {
        return this._minQuantity;
    }

    /**
     * @property isFlat
     * @description Returns true if the promotion is a flat discount.
     * @returns {boolean}
     */
    public get isFlat(): boolean {
        return this.discountFlat !== undefined && this.discountFlat > 0;
    }

    /**
     * @property isPercent
     * @description Returns true if the promotion is a percent discount.
     * @returns {boolean}
     */
    public get isPercent(): boolean {
        return this.discountPercent !== undefined && this.discountPercent > 0;
    }

    /**
     * @property hasMinAmount
     * @description Returns true if the promotion has a minimum amount.
     * @returns {boolean}
     */
    public get hasMinAmount(): boolean {
        return !!(this.minAmount && this.minAmount > 0);
    }

    /**
     * @property hasMinQuantityGreaterThanOne
     * @description Returns true if the promotion has a minimum quantity greater than one.
     * @returns {boolean}
     */
    public get hasMinQuantityGreaterThanOne(): boolean {
        return !!(this._minQuantity && this._minQuantity > 1);
    }

    /**
     * @property unappliedDescription
     * @description Returns the message to display when the promotion is not applied. (unchecked)
     * @returns {string}
     */
    public get unappliedDescription(): string {
        if (this.hasMinAmount && this.hasMinQuantityGreaterThanOne)
            return `Apply ${this.discount} OFF ${this.formattedMinAmount} when you buy ${this.minQuantity} of this item.`;

        if (this.hasMinAmount)
            return `Apply ${this.discount} OFF this item when you spend ${this.formattedMinAmount}.`;

        if (this.hasMinQuantityGreaterThanOne)
            return `Apply ${this.discount} OFF when you buy ${this.minQuantity} of this item.`;

        return `Apply ${this.discount} OFF this item.`;
    }

    /**
     * @property appliedDescription
     * @description Returns the message to display when the promotion is applied. (checked)
     * @returns {string}
     */
    public get appliedDescription(): string {
        if (this.hasMinAmount && this.hasMinQuantityGreaterThanOne)
            return `${this.discount} OFF ${this.formattedMinAmount} has been applied to your cart (min qty ${this.minQuantity})`;

        if (this.hasMinAmount)
            return `${this.discount} OFF this item when you spend ${this.formattedMinAmount} has been applied to your cart`;

        if (this.hasMinQuantityGreaterThanOne)
            return `${this.discount} OFF has been applied to your cart (min qty ${this.minQuantity})`;

        return `${this.discount} OFF this item has been applied to your cart.`;
    }
}